import { Folder } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from './useFetchFolderContents';
import { GET_FOLDER_QUERY_KEY } from './useGetFolder';
import { HAS_SUBFOLDER_DESCRIPTIONS_QUERY_KEY } from './useHasSubfolderDescriptions';
import { FOLDER_COLUMN_VISIBILITY_QUERY_KEY } from './useFolderColumnVisibility';

export const useUpdateFolderDescription = ({
  folder,
  onSuccess,
  onError,
}: {
  folder?: Folder;
  onSuccess?: (updatedFolder: Folder) => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();

  const { mutate, isPending, error } = useMutation({
    mutationKey: ['updateFolderDescription', folder?.id],
    mutationFn: (description: string) => {
      const trimmed = description.trim();
      if (trimmed === '') {
        return api.folders.deleteFolderDescription(tenantId, folder!.id);
      }
      return api.folders.updateFolderDescription(tenantId, folder!.id, { description: trimmed });
    },
    onSuccess: data => {
      onSuccess?.(data.data.folder);
      queryClient.resetQueries({
        queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
        exact: false,
      });
      queryClient.removeQueries({
        queryKey: [GET_FOLDER_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [HAS_SUBFOLDER_DESCRIPTIONS_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({
        queryKey: [FOLDER_COLUMN_VISIBILITY_QUERY_KEY],
        exact: false,
      });
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error.response?.data.message ?? 'Failed to update folder description');
    },
  });

  return {
    updateDescription: mutate,
    isUpdating: isPending,
    error,
  };
};
